import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Alert,
  Fab,
  Menu,
  MenuItem,
  Divider,
  Tab,
  Tabs,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Group,
  PersonAdd,
  ExitToApp,
  People,
  Search,
  ContentCopy,
  CheckCircle,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { groupService } from '../../services/groupService'
import { Group as GroupType, GroupMember, Transaction } from '../../types'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ConfirmDialog from '../../components/Common/ConfirmDialog'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

const groupSchema = yup.object({
  name: yup.string().required('Group name is required').min(2, 'Name must be at least 2 characters'),
  description: yup.string(),
})

const inviteSchema = yup.object({
  email: yup.string().required('Email is required').email('Invalid email format'),
})

type GroupFormData = yup.InferType<typeof groupSchema>
type InviteFormData = yup.InferType<typeof inviteSchema>

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`group-tabpanel-${index}`}
      aria-labelledby={`group-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

const GroupsPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [groups, setGroups] = useState<GroupType[]>([])
  const [selectedGroup, setSelectedGroup] = useState<GroupType | null>(null)
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([])
  const [groupTransactions, setGroupTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  
  // Dialog states
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<GroupType | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState<GroupType | null>(null)
  const [groupToLeave, setGroupToLeave] = useState<GroupType | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<GroupMember | null>(null)
  const [removeMemberConfirmOpen, setRemoveMemberConfirmOpen] = useState(false)
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedGroupForMenu, setSelectedGroupForMenu] = useState<GroupType | null>(null)
  
  // Invite states
  const [inviteCode, setInviteCode] = useState('')
  const [inviteCodeCopied, setInviteCodeCopied] = useState(false)

  // Forms
  const groupForm = useForm<GroupFormData>({
    resolver: yupResolver(groupSchema),
    defaultValues: {
      name: '',
      description: '',
    }
  })

  const inviteForm = useForm<InviteFormData>({
    resolver: yupResolver(inviteSchema),
    defaultValues: {
      email: '',
    }
  })

  const fetchGroups = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await groupService.getGroups()
      setGroups(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load groups')
    } finally {
      setLoading(false)
    }
  }

  const fetchGroupDetails = async (groupId: number) => {
    try {
      const [activity, transactions] = await Promise.all([
        groupService.getGroupActivity(groupId),
        groupService.getGroupTransactions(groupId, 1, 10)
      ])
      setGroupMembers(activity.memberActivity.map((member: any) => ({
        id: member.userId,
        groupId: groupId,
        userId: member.userId,
        firstName: member.userName.split(' ')[0] || '',
        lastName: member.userName.split(' ')[1] || '',
        email: '', // Not available from activity endpoint
        role: 'member' as const,
        joinedAt: new Date().toISOString(), // Not available from activity endpoint
      })))
      setGroupTransactions(transactions.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load group details')
    }
  }

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (user && localStorage.getItem('auth_token')) {
      console.log('GroupsPage: User authenticated, fetching groups')
      fetchGroups()
    } else {
      console.log('GroupsPage: User not authenticated, skipping fetch')
    }
  }, [user])

  useEffect(() => {
    // Only fetch group details if user is authenticated and group is selected
    if (selectedGroup && user && localStorage.getItem('auth_token')) {
      fetchGroupDetails(selectedGroup.id)
    }
  }, [selectedGroup, user])

  const handleGroupSubmit = async (data: GroupFormData) => {
    try {
      if (editingGroup) {
        const updated = await groupService.updateGroup(editingGroup.id, data)
        setGroups(prev => prev.map(group => group.id === editingGroup.id ? updated : group))
        if (selectedGroup?.id === editingGroup.id) {
          setSelectedGroup(updated)
        }
      } else {
        const newGroup = await groupService.createGroup(data)
        setGroups(prev => [...prev, newGroup])
      }
      setGroupDialogOpen(false)
      setEditingGroup(null)
      groupForm.reset()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save group')
    }
  }

  const handleInviteSubmit = async (_data: InviteFormData) => {
    if (!selectedGroup) return
    
    try {
      await groupService.inviteToGroup(selectedGroup.id)
      setInviteDialogOpen(false)
      inviteForm.reset()
      // Refresh group members
      fetchGroupDetails(selectedGroup.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation')
    }
  }

  const handleGenerateInviteCode = async () => {
    if (!selectedGroup) return
    
    try {
      const response = await groupService.inviteToGroup(selectedGroup.id)
      setInviteCode(response.inviteCode)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate invite code')
    }
  }

  const handleCopyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode)
    setInviteCodeCopied(true)
    setTimeout(() => setInviteCodeCopied(false), 2000)
  }

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return
    
    try {
      await groupService.deleteGroup(groupToDelete.id)
      setGroups(prev => prev.filter(g => g.id !== groupToDelete.id))
      if (selectedGroup?.id === groupToDelete.id) {
        setSelectedGroup(null)
      }
      setDeleteConfirmOpen(false)
      setGroupToDelete(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete group')
    }
  }

  const handleLeaveGroup = async () => {
    if (!groupToLeave || !user) return
    
    try {
      await groupService.removeMember(groupToLeave.id, user.id)
      setGroups(prev => prev.filter(g => g.id !== groupToLeave.id))
      if (selectedGroup?.id === groupToLeave.id) {
        setSelectedGroup(null)
      }
      setLeaveConfirmOpen(false)
      setGroupToLeave(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave group')
    }
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove || !selectedGroup) return
    
    console.log('Attempting to remove member:', {
      member: memberToRemove,
      memberRole: memberToRemove.role,
      selectedGroup: selectedGroup,
      isOwner: memberToRemove.role === 'owner',
      currentUser: user
    })
    
    // Prevent removing the group owner (check both role and user ID)
    if (memberToRemove.role === 'owner' || memberToRemove.userId === selectedGroup.ownerId) {
      console.log('Blocked: Cannot remove group owner')
      setError('Cannot remove the group owner. Transfer ownership first.')
      setRemoveMemberConfirmOpen(false)
      setMemberToRemove(null)
      return
    }
    
    try {
      console.log('Making API call to remove member:', selectedGroup.id, memberToRemove.userId)
      await groupService.removeMember(selectedGroup.id, memberToRemove.userId)
      setGroupMembers(prev => prev.filter(m => m.userId !== memberToRemove.userId))
      setRemoveMemberConfirmOpen(false)
      setMemberToRemove(null)
    } catch (err: any) {
      console.error('Error removing member:', err)
      // Extract error message from API response
      const errorMessage = err?.response?.data?.error?.message || 
                          err?.response?.data?.message || 
                          err?.message || 
                          'Failed to remove member'
      setError(errorMessage)
      setRemoveMemberConfirmOpen(false)
      setMemberToRemove(null)
    }
  }

  const openEditDialog = (group: GroupType) => {
    setEditingGroup(group)
    groupForm.reset({
      name: group.name,
      description: group.description || '',
    })
    setGroupDialogOpen(true)
    setAnchorEl(null)
  }

  const openDeleteConfirm = (group: GroupType) => {
    setGroupToDelete(group)
    setDeleteConfirmOpen(true)
    setAnchorEl(null)
  }

  const openLeaveConfirm = (group: GroupType) => {
    setGroupToLeave(group)
    setLeaveConfirmOpen(true)
    setAnchorEl(null)
  }

  const isGroupOwner = (group: GroupType) => {
    return user && group.ownerId === user.id
  }

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <LoadingSpinner />
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Groups
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Share expenses and manage finances together
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Groups List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: 'fit-content' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">My Groups</Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setGroupDialogOpen(true)}
                >
                  Create
                </Button>
              </Box>
              <TextField
                placeholder="Search groups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Box>
            
            {filteredGroups.length > 0 ? (
              <List sx={{ p: 0 }}>
                {filteredGroups.map((group, index) => (
                  <React.Fragment key={group.id}>
                    <ListItem
                      button
                      selected={selectedGroup?.id === group.id}
                      onClick={() => setSelectedGroup(group)}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                          '&:hover': {
                            bgcolor: 'primary.main',
                          }
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <Group />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={group.name}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {group.memberCount} members
                            </Typography>
                            {isGroupOwner(group) && (
                              <Chip label="Owner" size="small" variant="outlined" />
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation()
                            setAnchorEl(e.currentTarget)
                            setSelectedGroupForMenu(group)
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < filteredGroups.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Group sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {searchTerm ? 'No groups found' : 'No groups yet'}
                </Typography>
                {!searchTerm && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setGroupDialogOpen(true)}
                  >
                    Create Group
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Group Details */}
        <Grid item xs={12} md={8}>
          {selectedGroup ? (
            <Paper>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                  <Tab label="Overview" />
                  <Tab label={`Members (${groupMembers.length})`} />
                  <Tab label={`Transactions (${groupTransactions.length})`} />
                </Tabs>
              </Box>

              <TabPanel value={activeTab} index={0}>
                {/* Overview */}
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        {selectedGroup.name}
                      </Typography>
                      {selectedGroup.description && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {selectedGroup.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip
                          label={isGroupOwner(selectedGroup) ? 'Owner' : 'Member'}
                          color={isGroupOwner(selectedGroup) ? 'primary' : 'default'}
                          size="small"
                        />
                        <Chip
                          label={`${selectedGroup.memberCount} members`}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<PersonAdd />}
                        onClick={() => setInviteDialogOpen(true)}
                      >
                        Invite
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/transactions?groupId=${selectedGroup.id}`)}
                      >
                        View Transactions
                      </Button>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Group Balance
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {formatCurrency(selectedGroup.totalBalance || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total group expenses
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Recent Activity
                          </Typography>
                          <Typography variant="h4" color="info.main">
                            {groupTransactions.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Transactions this month
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                {/* Members */}
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">Group Members</Typography>
                    <Button
                      variant="contained"
                      startIcon={<PersonAdd />}
                      onClick={() => setInviteDialogOpen(true)}
                    >
                      Invite Member
                    </Button>
                  </Box>
                  
                  <List>
                    {groupMembers.map((member, index) => (
                      <React.Fragment key={member.userId}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar>
                              {member.firstName?.[0]}{member.lastName?.[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${member.firstName} ${member.lastName}`}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {member.email}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={member.role}
                                    size="small"
                                    color={member.role === 'owner' ? 'primary' : 'default'}
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={`Joined ${new Date(member.joinedAt).toLocaleDateString()}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                            }
                          />
                          {isGroupOwner(selectedGroup) && 
                           member.role !== 'owner' && 
                           member.userId !== selectedGroup.ownerId && (
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => {
                                  console.log('Remove button clicked for member:', member)
                                  setMemberToRemove(member)
                                  setRemoveMemberConfirmOpen(true)
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                        {index < groupMembers.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                {/* Transactions */}
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">Recent Transactions</Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/transactions?groupId=${selectedGroup.id}`)}
                    >
                      View All
                    </Button>
                  </Box>
                  
                  {groupTransactions.length > 0 ? (
                    <List>
                      {groupTransactions.map((transaction, index) => (
                        <React.Fragment key={transaction.id}>
                          <ListItem>
                            <ListItemText
                              primary={transaction.description}
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {transaction.categoryName} • {new Date(transaction.date).toLocaleDateString()}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Added by {transaction.userName}
                                  </Typography>
                                </Box>
                              }
                            />
                            <Typography
                              variant="body1"
                              sx={{
                                color: transaction.type === 'income' ? 'success.main' : 'error.main',
                                fontWeight: 'medium'
                              }}
                            >
                              {transaction.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                            </Typography>
                          </ListItem>
                          {index < groupTransactions.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No transactions yet in this group
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>
            </Paper>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <People sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Select a group to view details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a group from the list to see members, transactions, and manage settings
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', sm: 'none' }
        }}
        onClick={() => setGroupDialogOpen(true)}
      >
        <Add />
      </Fab>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {selectedGroupForMenu && isGroupOwner(selectedGroupForMenu) && (
          <MenuItem onClick={() => selectedGroupForMenu && openEditDialog(selectedGroupForMenu)}>
            <Edit sx={{ mr: 1 }} />
            Edit Group
          </MenuItem>
        )}
        <MenuItem onClick={() => selectedGroupForMenu && setSelectedGroup(selectedGroupForMenu)}>
          <Group sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {selectedGroupForMenu && isGroupOwner(selectedGroupForMenu) ? (
          <MenuItem 
            onClick={() => selectedGroupForMenu && openDeleteConfirm(selectedGroupForMenu)}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1 }} />
            Delete Group
          </MenuItem>
        ) : (
          <MenuItem 
            onClick={() => selectedGroupForMenu && openLeaveConfirm(selectedGroupForMenu)}
            sx={{ color: 'warning.main' }}
          >
            <ExitToApp sx={{ mr: 1 }} />
            Leave Group
          </MenuItem>
        )}
      </Menu>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={groupForm.handleSubmit(handleGroupSubmit)}>
          <DialogTitle>
            {editingGroup ? 'Edit Group' : 'Create Group'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Controller
                name="name"
                control={groupForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Group Name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="description"
                control={groupForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description (Optional)"
                    multiline
                    rows={2}
                    fullWidth
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGroupDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Member to {selectedGroup?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <form onSubmit={inviteForm.handleSubmit(handleInviteSubmit)}>
              <Controller
                name="email"
                control={inviteForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    type="email"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Button type="submit" variant="contained" fullWidth>
                Send Invitation
              </Button>
            </form>
            
            <Divider sx={{ my: 2 }}>OR</Divider>
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Generate an invite code to share:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleGenerateInviteCode}
                  disabled={!!inviteCode}
                >
                  Generate Code
                </Button>
                {inviteCode && (
                  <>
                    <TextField
                      value={inviteCode}
                      size="small"
                      InputProps={{ readOnly: true }}
                      sx={{ flexGrow: 1 }}
                    />
                    <IconButton onClick={handleCopyInviteCode}>
                      {inviteCodeCopied ? <CheckCircle color="success" /> : <ContentCopy />}
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteGroup}
        title="Delete Group"
        message={`Are you sure you want to delete "${groupToDelete?.name}"? This will permanently delete all group data including transactions and cannot be undone.`}
        confirmText="Delete"
        severity="error"
      />

      <ConfirmDialog
        open={leaveConfirmOpen}
        onCancel={() => setLeaveConfirmOpen(false)}
        onConfirm={handleLeaveGroup}
        title="Leave Group"
        message={`Are you sure you want to leave "${groupToLeave?.name}"? You will lose access to all group transactions and data.`}
        confirmText="Leave"
        severity="warning"
      />

      <ConfirmDialog
        open={removeMemberConfirmOpen}
        onCancel={() => setRemoveMemberConfirmOpen(false)}
        onConfirm={handleRemoveMember}
        title="Remove Member"
        message={`Are you sure you want to remove ${memberToRemove?.firstName} ${memberToRemove?.lastName} from the group?`}
        confirmText="Remove"
        severity="error"
      />
    </Container>
  )
}

export default GroupsPage